import { useState } from "react";
import { useAuth } from "../lib/AuthContext";

/**
 * Pantalla de cambio de contraseña. Se muestra en dos escenarios:
 *   1. Automáticamente cuando el usuario tiene `mustChangePassword=true`
 *      (primer login o tras un reset admin). En ese caso no puede navegar
 *      al resto de la app hasta hacerlo.
 *   2. A demanda desde el menú de usuario (props.forced=false, botón cancelar
 *      opcional). Fase 1 solo cubrimos el escenario forzado.
 */
export function ChangePassword({
  forced = true,
  onDone,
}: {
  forced?: boolean;
  onDone?: () => void;
}): JSX.Element {
  const { user, changePassword, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (next.length < 6) {
      setError("La nueva contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (next !== confirm) {
      setError("La confirmación no coincide.");
      return;
    }
    if (current === next) {
      setError("La nueva contraseña debe ser distinta de la actual.");
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      if (onDone) onDone();
    } catch (err) {
      const msg = (err as Error).message || "Error al cambiar la contraseña";
      if (msg === "bad_current" || msg.includes("bad_current")) {
        setError("La contraseña actual no es correcta.");
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-shell">
      <form className="login-card" onSubmit={onSubmit}>
        <div className="login-brand">
          <div className="brand-mark">EC</div>
          <div>
            <div className="brand-name">ConciliaEcilimp</div>
            <div className="brand-tag">Tesorería · Conciliación</div>
          </div>
        </div>
        <h1 className="login-title">
          {forced ? "Cambia tu contraseña" : "Cambiar contraseña"}
        </h1>
        {forced && (
          <p className="login-hint">
            Antes de continuar, {user?.displayName ?? "usuario"}, elige una contraseña
            personal. La actual es temporal.
          </p>
        )}
        <label className="login-field">
          <span>Contraseña actual</span>
          <input
            type="password"
            value={current}
            autoComplete="current-password"
            autoFocus
            onChange={(e) => setCurrent(e.target.value)}
            disabled={busy}
            required
          />
        </label>
        <label className="login-field">
          <span>Nueva contraseña</span>
          <input
            type="password"
            value={next}
            autoComplete="new-password"
            onChange={(e) => setNext(e.target.value)}
            disabled={busy}
            required
            minLength={6}
          />
        </label>
        <label className="login-field">
          <span>Repite la nueva contraseña</span>
          <input
            type="password"
            value={confirm}
            autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)}
            disabled={busy}
            required
            minLength={6}
          />
        </label>
        {error && <div className="login-error">{error}</div>}
        <div className="login-actions">
          <button type="submit" disabled={busy || !current || !next || !confirm}>
            {busy ? "Guardando…" : "Guardar y continuar"}
          </button>
          {forced ? (
            <button type="button" className="link-btn" onClick={() => void logout()}>
              Cerrar sesión
            </button>
          ) : (
            onDone && (
              <button type="button" onClick={onDone} disabled={busy}>
                Cancelar
              </button>
            )
          )}
        </div>
      </form>
    </div>
  );
}
